/**
 * Mobile Design System
 * Comprehensive design tokens and utilities for mobile-first experience
 */

export const mobileDesignSystem = {
  // Color Palette
  colors: {
    // Primary
    primary: {
      50: 'rgba(168, 85, 247, 0.1)',
      100: 'rgba(168, 85, 247, 0.2)',
      200: 'rgba(168, 85, 247, 0.3)',
      300: 'rgba(168, 85, 247, 0.4)',
      400: 'rgba(168, 85, 247, 0.6)',
      500: 'rgba(168, 85, 247, 0.8)',
      600: 'rgba(168, 85, 247, 1)',
    },
    // Success
    success: {
      50: 'rgba(34, 197, 94, 0.1)',
      100: 'rgba(34, 197, 94, 0.2)',
      200: 'rgba(34, 197, 94, 0.3)',
      300: 'rgba(34, 197, 94, 0.4)',
      400: 'rgba(34, 197, 94, 0.6)',
      500: 'rgba(34, 197, 94, 0.8)',
      600: 'rgba(34, 197, 94, 1)',
    },
    // Warning
    warning: {
      50: 'rgba(251, 191, 36, 0.1)',
      100: 'rgba(251, 191, 36, 0.2)',
      200: 'rgba(251, 191, 36, 0.3)',
      300: 'rgba(251, 191, 36, 0.4)',
      400: 'rgba(251, 191, 36, 0.6)',
      500: 'rgba(251, 191, 36, 0.8)',
      600: 'rgba(251, 191, 36, 1)',
    },
    // Danger
    danger: {
      50: 'rgba(239, 68, 68, 0.1)',
      100: 'rgba(239, 68, 68, 0.2)',
      200: 'rgba(239, 68, 68, 0.3)',
      300: 'rgba(239, 68, 68, 0.4)',
      400: 'rgba(239, 68, 68, 0.6)',
      500: 'rgba(239, 68, 68, 0.8)',
      600: 'rgba(239, 68, 68, 1)',
    },
    // Neutral
    neutral: {
      50: 'rgba(156, 163, 175, 0.1)',
      100: 'rgba(156, 163, 175, 0.2)',
      200: 'rgba(156, 163, 175, 0.3)',
      300: 'rgba(156, 163, 175, 0.4)',
      400: 'rgba(156, 163, 175, 0.6)',
      500: 'rgba(156, 163, 175, 0.8)',
      600: 'rgba(156, 163, 175, 1)',
    },
    // Backgrounds
    background: {
      primary: 'rgba(0, 0, 0, 0.95)',
      secondary: 'rgba(17, 24, 39, 0.95)',
      tertiary: 'rgba(31, 41, 55, 0.95)',
      card: 'rgba(17, 24, 39, 0.95)',
      modal: 'rgba(17, 24, 39, 0.98)',
    },
    // Borders
    border: {
      primary: 'rgba(168, 85, 247, 0.6)',
      secondary: 'rgba(75, 85, 99, 0.6)',
      success: 'rgba(34, 197, 94, 0.6)',
      danger: 'rgba(239, 68, 68, 0.6)',
    }
  },

  // Spacing
  spacing: {
    xs: '0.5rem',    // 8px
    sm: '0.75rem',   // 12px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
  },

  // Border Radius
  borderRadius: {
    sm: '0.5rem',    // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.5rem',    // 24px
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.3)',
    md: '0 4px 16px rgba(0, 0, 0, 0.4)',
    lg: '0 8px 32px rgba(0, 0, 0, 0.5)',
    neon: {
      primary: '0 0 8px rgba(168, 85, 247, 0.6)',
      success: '0 0 8px rgba(34, 197, 94, 0.6)',
      danger: '0 0 8px rgba(239, 68, 68, 0.6)',
      warning: '0 0 8px rgba(251, 191, 36, 0.6)',
    }
  },

  // Typography
  typography: {
    sizes: {
      xs: '0.75rem',   // 12px
      sm: '0.875rem',  // 14px
      base: '1rem',    // 16px
      lg: '1.125rem',  // 18px
      xl: '1.25rem',   // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
    },
    weights: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    }
  },

  // Animations
  animations: {
    duration: {
      fast: '0.15s',
      normal: '0.25s',
      slow: '0.3s',
      slower: '0.5s',
    },
    easing: {
      easeOut: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0.0, 1, 1)',
      easeInOut: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    }
  },

  // Layout
  layout: {
    navbarHeight: '80px',
    safeAreaBottom: '24px',
    modalPadding: '120px',
    cardPadding: '1.5rem',
  }
};

export const ysr2Mobile = {
  shell: `
    relative min-h-screen overflow-hidden bg-[#050816] text-white
    bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.22),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.18),transparent_38%),linear-gradient(180deg,#020617_0%,#050816_44%,#020617_100%)]
  `,
  surface: `
    rounded-[1.75rem] border border-blue-400/20 bg-white/[0.055]
    shadow-[0_20px_70px_rgba(15,23,42,0.65)] backdrop-blur-xl
  `,
  surfaceStrong: `
    rounded-[2rem] border border-blue-300/25 bg-slate-950/70
    shadow-[0_24px_90px_rgba(37,99,235,0.16)] backdrop-blur-2xl
  `,
  glowCard: `
    rounded-[1.75rem] border border-purple-400/25 bg-gradient-to-br from-white/[0.085] to-white/[0.035]
    shadow-[0_20px_60px_rgba(88,28,135,0.22)] backdrop-blur-xl
  `,
  liveCard: `
    rounded-[1.75rem] border border-emerald-300/25 bg-gradient-to-br from-emerald-500/12 to-blue-500/8
    shadow-[0_18px_60px_rgba(16,185,129,0.18)] backdrop-blur-xl
  `,
  dangerCard: `
    rounded-[1.75rem] border border-red-300/25 bg-gradient-to-br from-red-500/12 to-pink-500/8
    shadow-[0_18px_60px_rgba(239,68,68,0.18)] backdrop-blur-xl
  `,
  amberCard: `
    rounded-[1.75rem] border border-amber-300/25 bg-gradient-to-br from-amber-500/12 to-orange-500/8
    shadow-[0_18px_60px_rgba(245,158,11,0.16)] backdrop-blur-xl
  `,
  pill: `
    inline-flex items-center rounded-full border border-blue-300/25 bg-blue-500/10
    px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-blue-100
  `,
  primaryButton: `
    rounded-2xl border border-blue-300/30 bg-blue-600 text-white
    shadow-[0_16px_45px_rgba(37,99,235,0.32)] transition-all
    hover:bg-blue-500 active:scale-[0.98]
  `,
  secondaryButton: `
    rounded-2xl border border-white/10 bg-white/[0.07] text-white
    shadow-[0_12px_35px_rgba(15,23,42,0.35)] transition-all
    hover:bg-white/[0.1] active:scale-[0.98]
  `,
  input: `
    rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-white
    placeholder:text-slate-500 focus:border-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20
  `,
  orbBlue: 'pointer-events-none absolute -top-24 -left-20 h-72 w-72 rounded-full bg-blue-600/20 blur-3xl',
  orbPurple: 'pointer-events-none absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-purple-600/20 blur-3xl',
};

// Utility functions
export const getMobileStyles = {
  // Card styles
  card: (variant = 'default') => {
    const base = `
      bg-gray-900/95 backdrop-blur-sm border-2 rounded-2xl p-6
    `;
    
    const variants = {
      default: 'border-purple-500/60 shadow-[0_8px_32px_rgba(168,85,247,0.3)]',
      success: 'border-green-500/60 shadow-[0_8px_32px_rgba(34,197,94,0.3)]',
      danger: 'border-red-500/60 shadow-[0_8px_32px_rgba(239,68,68,0.3)]',
      warning: 'border-yellow-500/60 shadow-[0_8px_32px_rgba(251,191,36,0.3)]',
      live: 'border-purple-500/60', // No glow for live dashboard cards
      ysr2: ysr2Mobile.surface,
      ysr2Strong: ysr2Mobile.surfaceStrong,
      ysr2Glow: ysr2Mobile.glowCard,
      ysr2Live: ysr2Mobile.liveCard,
      ysr2Danger: ysr2Mobile.dangerCard,
      ysr2Amber: ysr2Mobile.amberCard,
    };
    
    return `${base} ${variants[variant]}`;
  },

  // Button styles
  button: (variant = 'primary', size = 'md') => {
    const base = `
      font-semibold transition-all duration-200 rounded-xl
      backdrop-blur-sm border-2 shadow-lg
    `;
    
    const variants = {
      primary: `
        bg-blue-600 border-blue-300/30 text-white
        hover:bg-blue-500 hover:border-blue-200/50
        shadow-[0_16px_45px_rgba(37,99,235,0.32)]
        hover:shadow-[0_18px_52px_rgba(37,99,235,0.42)]
      `,
      secondary: `
        bg-white/[0.07] border-white/10 text-gray-200
        hover:bg-white/[0.1] hover:border-white/20
        shadow-[0_12px_35px_rgba(15,23,42,0.35)]
      `,
      success: `
        bg-green-900/95 border-green-500/60 text-white
        hover:bg-green-800/95 hover:border-green-400/60
        shadow-[0_4px_20px_rgba(34,197,94,0.3)]
      `,
      danger: `
        bg-red-900/95 border-red-500/60 text-white
        hover:bg-red-800/95 hover:border-red-400/60
        shadow-[0_4px_20px_rgba(239,68,68,0.3)]
      `,
    };
    
    const sizes = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-6 py-4 text-lg',
    };
    
    return `${base} ${variants[variant]} ${sizes[size]}`;
  },

  // Input styles
  input: () => `
    w-full px-4 py-3 bg-black/35 border-2 border-white/10
    rounded-2xl text-white text-center text-lg font-mono
    focus:border-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20
    transition-all duration-200
    placeholder:text-gray-400
  `,

  // Icon styles
  icon: (color = 'primary', size = 'md') => {
    const colors = {
      primary: 'text-purple-400 drop-shadow-[0_0_8px_rgba(168,85,247,0.6)]',
      success: 'text-green-400 drop-shadow-[0_0_8px_rgba(34,197,94,0.6)]',
      danger: 'text-red-400 drop-shadow-[0_0_8px_rgba(239,68,68,0.6)]',
      warning: 'text-yellow-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]',
      neutral: 'text-gray-400 drop-shadow-[0_0_4px_rgba(156,163,175,0.3)]',
    };
    
    const sizes = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
      xl: 'w-8 h-8',
    };
    
    return `${colors[color]} ${sizes[size]}`;
  },

  // Animation variants
  animation: {
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.25, ease: "easeOut" }
    },
    slideUp: {
      initial: { y: 30, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: 30, opacity: 0 },
      transition: { duration: 0.3, ease: "easeOut" }
    },
    scaleIn: {
      initial: { scale: 0.95, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 0.95, opacity: 0 },
      transition: { duration: 0.25, ease: "easeOut" }
    }
  }
};

export default mobileDesignSystem;
